import {
  FormFieldSummaryRowSchema,
  PlaytestDmDispatchRowSchema,
  PlaytestEventLogRowSchema,
  PlaytestEventTimelineRowSchema,
  PlaytestSentimentSummaryRowSchema,
  PlaytestSessionRowSchema,
  PlaytestSessionSentimentRowSchema,
  type FormFieldSummary,
  type PlaytestDmDispatch,
  type PlaytestEventLog,
  type PlaytestEventTimeline,
  type PlaytestSentimentSummary,
  type PlaytestSession,
  type PlaytestSessionSentiment,
  type PlaytestSessionState,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Read-only data layer for PR 5's session browse.
//
// RLS keeps everything scoped to the caller's studios. No writes here -
// session state and event-log rows are bot-owned. The timeline merges
// three sibling tables; we read them in parallel and stitch client-side
// to keep the wire payloads small and predictable.
//
// Build attribution comes from the build_markers primitive
// (game-settings → builds). Sessions carry `build_marker_id`; we join
// to surface the marker label/version. Null markers are rendered as
// "Unregistered build" in the UI with a CTA to register one.
// ---------------------------------------------------------------------------

const SESSIONS_TABLE = 'playtest_sessions';
const EVENT_LOG_TABLE = 'playtest_event_log';
const DISPATCH_TABLE = 'playtest_dm_dispatch';
const FEEDBACK_POOL_TABLE = 'feedback_pool';
const EVENTS_TABLE = 'playtest_events';

export interface BuildMarkerSummary {
  id: string;
  label: string;
  version: string;
  stage: BuildStageSummary | null;
}

export interface BuildStageSummary {
  id: string;
  label: string;
  colour: string | null;
}

// 'unattributed' filters to sessions whose build_marker_id is null
// (player wasn't on a registered build when the session started).
export type BuildMarkerFilter = string | 'unattributed' | null;

export interface SessionListFilters {
  tester: string | null;
  state: PlaytestSessionState | null;
  startedAfter: string | null;
  buildMarkerId: BuildMarkerFilter;
}

export interface SessionListRow {
  session: PlaytestSession;
  testerDisplayName: string | null;
  buildMarker: BuildMarkerSummary | null;
}

function parseStageJoin(raw: unknown): BuildStageSummary | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;
  const r = raw as { id?: string; label?: string; colour?: string | null };
  if (typeof r.id !== 'string' || typeof r.label !== 'string') return null;
  return {
    id: r.id,
    label: r.label,
    colour: typeof r.colour === 'string' ? r.colour : null,
  };
}

function parseMarkerJoin(raw: unknown): BuildMarkerSummary | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;
  const r = raw as {
    id?: string;
    label?: string;
    version?: string;
    game_dev_stages?: unknown;
  };
  if (typeof r.id !== 'string' || typeof r.label !== 'string' || typeof r.version !== 'string') {
    return null;
  }
  return {
    id: r.id,
    label: r.label,
    version: r.version,
    stage: parseStageJoin(r.game_dev_stages),
  };
}

export async function fetchPlaytestSessions(
  playtestId: string,
  filters: SessionListFilters,
): Promise<SessionListRow[]> {
  let query = supabase
    .from(SESSIONS_TABLE)
    .select(
      `
      *,
      player_tokens:player_token_id ( display_name, discord_user_id ),
      build_markers:build_marker_id (
        id, label, version,
        game_dev_stages:stage_id ( id, label, colour )
      )
    `,
    )
    .eq('playtest_id', playtestId)
    .order('last_activity_at', { ascending: false });
  if (filters.state !== null) {
    query = query.eq('state', filters.state);
  }
  if (filters.startedAfter !== null) {
    query = query.gte('started_at', filters.startedAfter);
  }
  if (filters.buildMarkerId === 'unattributed') {
    query = query.is('build_marker_id', null);
  } else if (filters.buildMarkerId !== null) {
    query = query.eq('build_marker_id', filters.buildMarkerId);
  }

  const { data, error } = await query;
  if (error !== null) {
    console.warn('[playtest-sessions] list query failed:', error.message);
    return [];
  }

  const out: SessionListRow[] = [];
  for (const raw of data ?? []) {
    const parsed = PlaytestSessionRowSchema.safeParse(raw);
    if (!parsed.success) continue;
    const tokenJoin = (raw as { player_tokens?: { display_name?: string | null } | null })
      .player_tokens;
    const displayName =
      tokenJoin !== null && tokenJoin !== undefined && typeof tokenJoin.display_name === 'string'
        ? tokenJoin.display_name
        : null;
    const markerJoin = (raw as { build_markers?: unknown }).build_markers;
    out.push({
      session: parsed.data,
      testerDisplayName: displayName,
      buildMarker: parseMarkerJoin(markerJoin),
    });
  }

  if (filters.tester !== null && filters.tester.length > 0) {
    const needle = filters.tester.toLowerCase();
    return out.filter((row) => {
      const fields: string[] = [row.session.discordUserId.toLowerCase()];
      if (row.testerDisplayName !== null) {
        fields.push(row.testerDisplayName.toLowerCase());
      }
      return fields.some((field) => field.includes(needle));
    });
  }

  return out;
}

export async function fetchSessionDetail(sessionId: string): Promise<SessionListRow | null> {
  const { data, error } = await supabase
    .from(SESSIONS_TABLE)
    .select(
      `
      *,
      player_tokens:player_token_id ( display_name, discord_user_id ),
      build_markers:build_marker_id (
        id, label, version,
        game_dev_stages:stage_id ( id, label, colour )
      )
    `,
    )
    .eq('id', sessionId)
    .maybeSingle();
  if (error !== null) {
    console.warn('[playtest-sessions] detail query failed:', error.message);
    return null;
  }
  if (data === null) return null;
  const parsed = PlaytestSessionRowSchema.safeParse(data);
  if (!parsed.success) return null;
  const tokenJoin = (data as { player_tokens?: { display_name?: string | null } | null })
    .player_tokens;
  const displayName =
    tokenJoin !== null && tokenJoin !== undefined && typeof tokenJoin.display_name === 'string'
      ? tokenJoin.display_name
      : null;
  const markerJoin = (data as { build_markers?: unknown }).build_markers;
  return {
    session: parsed.data,
    testerDisplayName: displayName,
    buildMarker: parseMarkerJoin(markerJoin),
  };
}

export async function fetchActiveSessionCountsByPlaytest(
  gameId: string,
): Promise<Record<string, { active: number; lastActivityAt: string | null }>> {
  const { data, error } = await supabase
    .from(SESSIONS_TABLE)
    .select('playtest_id, state, last_activity_at')
    .eq('game_id', gameId);
  if (error !== null) {
    console.warn('[playtest-sessions] active-counts query failed:', error.message);
    return {};
  }
  const out: Record<string, { active: number; lastActivityAt: string | null }> = {};
  for (const row of data ?? []) {
    const playtestId = typeof row.playtest_id === 'string' ? row.playtest_id : null;
    if (playtestId === null) continue;
    if (!(playtestId in out)) {
      out[playtestId] = { active: 0, lastActivityAt: null };
    }
    const bucket = out[playtestId]!;
    if (row.state === 'in_progress') bucket.active += 1;
    const last = typeof row.last_activity_at === 'string' ? row.last_activity_at : null;
    if (last !== null && (bucket.lastActivityAt === null || last > bucket.lastActivityAt)) {
      bucket.lastActivityAt = last;
    }
  }
  return out;
}

export interface TimelineEventCard {
  kind: 'event';
  at: string;
  eventLog: PlaytestEventLog;
  eventLabel: string;
  eventHeadline: string;
}

export interface TimelineDispatchCard {
  kind: 'dispatch';
  at: string;
  dispatch: PlaytestDmDispatch;
}

export interface TimelineResponseCard {
  kind: 'response';
  at: string;
  feedbackPoolId: string;
  questionId: string | null;
  questionPrompt: string | null;
  questionKind: string | null;
  questionConfig: Record<string, unknown> | null;
  rawText: string;
}

export type TimelineCard = TimelineEventCard | TimelineDispatchCard | TimelineResponseCard;

export async function fetchSessionTimeline(sessionId: string): Promise<TimelineCard[]> {
  const [logsResp, dispatchesResp, responsesResp] = await Promise.all([
    supabase
      .from(EVENT_LOG_TABLE)
      .select('*')
      .eq('playtest_session_id', sessionId)
      .order('occurred_at', { ascending: false }),
    supabase.from(DISPATCH_TABLE).select('*').eq('playtest_session_id', sessionId),
    supabase
      .from(FEEDBACK_POOL_TABLE)
      .select(
        `
        id, raw_text, captured_at, playtest_question_id,
        playtest_questions:playtest_question_id ( prompt, kind, config )
      `,
      )
      .eq('playtest_session_id', sessionId),
  ]);
  if (logsResp.error !== null) {
    console.warn('[playtest-sessions] event_log fetch failed:', logsResp.error.message);
  }
  if (dispatchesResp.error !== null) {
    console.warn('[playtest-sessions] dispatch fetch failed:', dispatchesResp.error.message);
  }
  if (responsesResp.error !== null) {
    console.warn('[playtest-sessions] feedback fetch failed:', responsesResp.error.message);
  }

  const logs: PlaytestEventLog[] = [];
  for (const raw of logsResp.data ?? []) {
    const parsed = PlaytestEventLogRowSchema.safeParse(raw);
    if (parsed.success) logs.push(parsed.data);
  }
  const dispatches: PlaytestDmDispatch[] = [];
  for (const raw of dispatchesResp.data ?? []) {
    const parsed = PlaytestDmDispatchRowSchema.safeParse(raw);
    if (parsed.success) dispatches.push(parsed.data);
  }

  const eventIds = Array.from(new Set(logs.map((l) => l.eventId)));
  const eventMeta = new Map<string, { label: string; headline: string }>();
  if (eventIds.length > 0) {
    const { data: eventRows, error: eventsErr } = await supabase
      .from(EVENTS_TABLE)
      .select('id, label, headline')
      .in('id', eventIds);
    if (eventsErr !== null) {
      console.warn('[playtest-sessions] event meta fetch failed:', eventsErr.message);
    }
    for (const raw of eventRows ?? []) {
      if (typeof raw.id !== 'string') continue;
      eventMeta.set(raw.id, {
        label: typeof raw.label === 'string' ? raw.label : '',
        headline: typeof raw.headline === 'string' ? raw.headline : '',
      });
    }
  }

  const cards: TimelineCard[] = [];
  for (const log of logs) {
    const meta = eventMeta.get(log.eventId);
    cards.push({
      kind: 'event',
      at: log.occurredAt,
      eventLog: log,
      eventLabel: meta?.label ?? log.eventName,
      eventHeadline: meta?.headline ?? '',
    });
  }
  for (const dispatch of dispatches) {
    cards.push({
      kind: 'dispatch',
      at: dispatch.respondedAt ?? dispatch.sentAt ?? dispatch.createdAt,
      dispatch,
    });
  }
  for (const raw of responsesResp.data ?? []) {
    if (typeof raw.id !== 'string') continue;
    if (typeof raw.raw_text !== 'string') continue;
    if (typeof raw.captured_at !== 'string') continue;
    const join = (
      raw as {
        playtest_questions?: {
          prompt?: string;
          kind?: string;
          config?: Record<string, unknown> | null;
        } | null;
      }
    ).playtest_questions;
    cards.push({
      kind: 'response',
      at: raw.captured_at,
      feedbackPoolId: raw.id,
      questionId: typeof raw.playtest_question_id === 'string' ? raw.playtest_question_id : null,
      questionPrompt:
        join !== null && join !== undefined && typeof join.prompt === 'string' ? join.prompt : null,
      questionKind:
        join !== null && join !== undefined && typeof join.kind === 'string' ? join.kind : null,
      questionConfig:
        join !== null &&
        join !== undefined &&
        join.config !== null &&
        typeof join.config === 'object'
          ? (join.config as Record<string, unknown>)
          : null,
      rawText: raw.raw_text,
    });
  }
  cards.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  return cards;
}

export type ExpireSessionResult =
  | { ok: true; session: PlaytestSession }
  | {
      ok: false;
      error: 'not_authenticated' | 'not_a_member' | 'session_not_found' | 'unknown';
    };

export async function expirePlaytestSession(sessionId: string): Promise<ExpireSessionResult> {
  const { data, error } = await supabase.rpc('expire_playtest_session', {
    p_session_id: sessionId,
  });
  if (error !== null) {
    const msg = error.message ?? '';
    if (msg.includes('not_authenticated')) return { ok: false, error: 'not_authenticated' };
    if (msg.includes('not_a_member')) return { ok: false, error: 'not_a_member' };
    if (msg.includes('session_not_found')) return { ok: false, error: 'session_not_found' };
    console.warn('[playtest-sessions] expire failed:', error.message);
    return { ok: false, error: 'unknown' };
  }
  const parsed = PlaytestSessionRowSchema.safeParse(data);
  if (!parsed.success) return { ok: false, error: 'unknown' };
  return { ok: true, session: parsed.data };
}

// Page-level realtime helper. playtest_event_log has no game_id column,
// so we filter by playtest_id and let the calling page own the lifecycle.
// (The shell context owns the playtest_sessions subscription.)
export function subscribeEventLogForPlaytest(playtestId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`playtest_event_log:${playtestId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: EVENT_LOG_TABLE,
        filter: `playtest_id=eq.${playtestId}`,
      },
      () => {
        onChange();
      },
    )
    .subscribe();
  return () => {
    void supabase.removeChannel(channel);
  };
}

// ---------------------------------------------------------------------------
// Derived playtest sentiment (migration 0106).
//
// Read-side aggregates of the existing per-record sentiment enrichments on
// the session's answered-question feedback_pool rows, fetched via dedicated
// security-definer RPCs alongside the direct table reads above. No new
// write path; aggregates existing enrichments.
// ---------------------------------------------------------------------------

// Overall sentiment for one session. Returns null on failure; score/band
// are null inside the row when nothing scored yet.
export async function fetchPlaytestSessionSentiment(
  sessionId: string,
): Promise<PlaytestSessionSentiment | null> {
  const { data, error } = await supabase.rpc('get_playtest_session_sentiment', {
    p_session_id: sessionId,
  });
  if (error !== null) {
    console.warn('[playtest-sessions] session sentiment load failed:', error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) return null;
  const parsed = PlaytestSessionSentimentRowSchema.safeParse(row);
  if (!parsed.success) return null;
  return parsed.data;
}

// Playtest-flow sentiment aggregate over all sessions. Returns null on
// failure.
export async function fetchPlaytestSentimentSummary(
  playtestId: string,
): Promise<PlaytestSentimentSummary | null> {
  const { data, error } = await supabase.rpc('get_playtest_sentiment_summary', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtest-sessions] playtest sentiment summary load failed:', error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) return null;
  const parsed = PlaytestSentimentSummaryRowSchema.safeParse(row);
  if (!parsed.success) return null;
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Flow-level event aggregation (migration 0112).
//
// Powers the satisfaction timeline + per-event summary cards above the
// session table, and the click-to-expand per-question drill-down. Both read
// on the server; no writes here.
// ---------------------------------------------------------------------------

// One row per live event in the flow, ordered by order_index. Returns [] on
// failure or when the flow has no events. Drops any row that fails to parse
// so one bad row never blanks the whole timeline.
export async function fetchPlaytestEventTimeline(
  playtestId: string,
): Promise<PlaytestEventTimeline[]> {
  const { data, error } = await supabase.rpc('get_playtest_event_timeline', {
    p_playtest_id: playtestId,
  });
  if (error !== null) {
    console.warn('[playtest-sessions] event timeline load failed:', error.message);
    return [];
  }
  if (!Array.isArray(data)) return [];
  const rows: PlaytestEventTimeline[] = [];
  for (const raw of data) {
    const parsed = PlaytestEventTimelineRowSchema.safeParse(raw);
    if (parsed.success) rows.push(parsed.data);
  }
  return rows;
}

// Per-question quantitative summary for one event. Same shape as the form
// field summary, so it parses with FormFieldSummaryRowSchema and renders with
// the shared field-summary list. Returns null on failure.
export async function fetchPlaytestEventFieldSummary(
  eventId: string,
): Promise<FormFieldSummary | null> {
  const { data, error } = await supabase.rpc('get_playtest_event_field_summary', {
    p_event_id: eventId,
  });
  if (error !== null) {
    console.warn('[playtest-sessions] event field summary load failed:', error.message);
    return null;
  }
  // Single jsonb document; supabase-js may hand it back directly or wrapped in
  // a one-element array, so unwrap defensively as fetchFormFieldSummary does.
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) return null;
  const parsed = FormFieldSummaryRowSchema.safeParse(row);
  if (!parsed.success) {
    console.warn('[playtest-sessions] event field summary parse failed:', parsed.error.issues);
    return null;
  }
  return parsed.data;
}
